import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export function initHeroZoom() {
  // ---- Two-scroll page navigation -------------------------------------
  // Free scrolling is disabled. Each wheel/touch gesture either:
  //   1) Locks (snaps) the page to the nearest section, OR
  //   2) If the previous gesture (in the same direction) already locked
  //      the page, advances to the next section.
  // i.e. you need to scroll twice to move between pages.
  const sections = ['#top', '#next', '#work']
    .map((sel) => document.querySelector(sel))
    .filter(Boolean);

  if (sections.length) {
    const lenis = window.__lenis;
    // Stop Lenis from consuming wheel/touch input — we drive scroll via
    // lenis.scrollTo({ force: true }) instead.
    if (lenis && typeof lenis.stop === 'function') lenis.stop();

    let currentIndex = 0;
    let pendingDir = 0;
    let isAnimating = false;
    let lastGestureAt = 0;
    let accum = 0;

    const RESET_MS = 900;
    const GESTURE_DELTA = 30;
    const GESTURE_COOLDOWN_MS = 250;

    function targetY(index) {
      const el = sections[index];
      if (!el) return 0;
      return el.getBoundingClientRect().top + window.scrollY;
    }

    function nearestIndex() {
      const y = window.scrollY;
      let best = 0;
      let bestDist = Infinity;
      sections.forEach((el, i) => {
        const sy = el.getBoundingClientRect().top + window.scrollY;
        const d = Math.abs(sy - y);
        if (d < bestDist) { best = i; bestDist = d; }
      });
      return best;
    }

    function animateTo(index) {
      isAnimating = true;
      currentIndex = index;
      const done = () => { isAnimating = false; };
      if (lenis && typeof lenis.scrollTo === 'function') {
        lenis.scrollTo(targetY(index), {
          duration: 0.9,
          force: true,
          easing: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
          onComplete: done,
        });
      } else {
        gsap.to(window, {
          scrollTo: { y: targetY(index), autoKill: false },
          duration: 0.9,
          ease: 'power3.inOut',
          onComplete: done,
        });
      }
    }

    function fireGesture(dir) {
      const now = performance.now();
      if (now - lastGestureAt < GESTURE_COOLDOWN_MS) return;
      lastGestureAt = now;

      const here = nearestIndex();
      currentIndex = here;

      const onEdge =
        (dir > 0 && here === sections.length - 1) ||
        (dir < 0 && here === 0);
      if (onEdge) return;

      const next = here + dir;
      animateTo(next);
    }

    function onWheel(e) {
      e.preventDefault();
      if (isAnimating) return;
      const now = performance.now();
      if (now - lastGestureAt > RESET_MS) accum = 0;
      accum += e.deltaY;
      if (Math.abs(accum) >= GESTURE_DELTA) {
        const dir = accum > 0 ? 1 : -1;
        accum = 0;
        fireGesture(dir);
      }
    }
    window.addEventListener('wheel', onWheel, { passive: false });

    let touchStartY = null;
    window.addEventListener('touchstart', (e) => {
      touchStartY = e.touches[0].clientY;
    }, { passive: true });
    window.addEventListener('touchmove', (e) => {
      if (isAnimating) e.preventDefault();
    }, { passive: false });
    window.addEventListener('touchend', (e) => {
      if (touchStartY == null) return;
      const endY = (e.changedTouches[0] || {}).clientY ?? touchStartY;
      const dy = touchStartY - endY;
      touchStartY = null;
      if (Math.abs(dy) > 30) fireGesture(dy > 0 ? 1 : -1);
    }, { passive: true });

    window.addEventListener('keydown', (e) => {
      if (isAnimating) return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault();
        fireGesture(1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        fireGesture(-1);
      }
    });
  }

  // ---- Subtle fade-up reveals as each section enters viewport -----------
  const reveals = [
    { sel: '.next__logo',      stagger: 0,    delay: 0 },
    { sel: '.next__copy > *',  stagger: 0.09, delay: 0.05 },
    { sel: '.work__eyebrow',   stagger: 0,    delay: 0 },
    { sel: '.work__row',       stagger: 0.07, delay: 0.05 },
  ];

  reveals.forEach(({ sel, stagger, delay }) => {
    const els = document.querySelectorAll(sel);
    if (!els.length) return;
    gsap.from(els, {
      opacity: 0,
      y: 26,
      duration: 0.85,
      ease: 'power2.out',
      stagger,
      delay,
      scrollTrigger: {
        trigger: els[0].closest('section') || els[0],
        start: 'top 80%',
        toggleActions: 'play none none reverse',
      },
    });
  });
}
